import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { UIElement } from '../ui-element';
import { ComponentState } from '../ui-element.const';
import { UIButtonbar } from '../buttonbar/ui-buttonbar';
import { browser } from '../../global/browser';
import { Digest } from '../../global/digest';
import { Labels } from '../../global/labels';
import { TabIndex } from '../../global/keyboard';
import {
    runInNextFrame,
    throttle,
    waitForAnimations,
} from '../../global/helpers';

import {
    dispatchCustomEvent,
    updateElement,
    isVisible,
    closestByCond,
    isParentOf,
    PrimaryInteractiveElements,
    SecondaryInteractiveElements,
} from '../../global/ui-helpers';
import type {
    Attributes,
    ElementChild,
    ElementConfig,
} from '../ui-element.type';
import type { EventHandler, NullOr } from '../../global/utils.type';
import type { Activatable } from '../../types/common-interfaces.type';
import type { TranslationLabels } from '../../types/labels.type';
import type {
    UIContentShadow,
    UIModalActionTypes,
    UIModalAlignment,
    UIModalAlignments,
    UIModalLayout,
    UIModalLayouts,
    UIModalScrollIncrementSettings,
    UIModalType,
    UIModalTypes,
    UIModalWidth,
} from './ui-modal.types';
import styles from './ui-modal.css';
import { UIModalController } from './ui-modal-controller';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @implements {Activatable}
 * @alias UIModal
 * @element ui-modal
 * @classdesc Represents a class for <code>ui-modal</code> element
 * @fires event:modal-open-enter
 * @fires event:modal-close
 * @fires event:modal-close-enter
 * @fires event:modal-action
 * @fires event:modal-back
 * @fires event:modal-updatelist
 * @property {UIModalType} type
 * {@attr type} The type of modal window.
 *  {@desc alert: alert modal}
 *  {@desc confirmation: modal with yes/no actions}
 *  {@desc popover: represents popover element like tooltip or annotation}
 *  {@desc bubble: represents modal as bubble such as toast or notification}
 *  {@desc menu: overflow menu modal}
 * @property {boolean} [detached] {@attr detached} Modal will be added to
 * the end of the body and after close will be returned.
 * @property {UIModalLayout} [layout] {@attr layout} Layout of the modal dialog window.
 *  {@desc fluid: fluid modal layout, stretched to the size of content}
 *  {@desc logout: Used only for logout windows.}
 * @property {boolean} [compact] {@attr compact} Indicate should the cross button be hidden
 * @property {boolean} [shifted] {@attr shifted} Indicate should the modal be shifted
 * (Is used for multiple windows).
 * @property {boolean} [dynamic] {@attr dynamic}
 * @property {boolean} [closable] {@attr closable} Defines if modal window could be closed using
 * by clicking on overlay.
 * @property {boolean} [noclosable] {@attr noclosable} Opposite of closable.
 * @property {string} [modalTitle] {@attr modal-title} The title in the modal header.
 * @property {string} [modalSubtitle] {@attr modal-subtitle}The subtitle in the modal header.
 * @property {boolean} [animation] {@attr animation} true - to force animation to be played
 * @property {string} [labelAccept="Yes"] {@attr label-accept} text label for the accept button
 * (Is used for confirmation windows).
 * @property {string} [labelDecline="No"] {@attr label-decline} text label for the decline button
 * (Is used for confirmation windows).
 * @property {string} [labelDismiss="Ok"] {@attr label-dismiss} text label for the dismiss button
 * (Is used for alerts).
 * @property {boolean} [bottomSheet] {@attr bottom-sheet} Modals opens from the bottom on mobile
 * devices.
 * @property {boolean} [noBottomSheet] {@attr no-bottom-sheet} Opt-out of the default bottom-sheet
 * behavior for menu type modals on mobile devices.
 * @property {boolean} [backAction] {@attr back-action} Renders a back button in the modal
 * header. Clicking it fires a 'modal-back' event so the consumer can navigate between views.
 * The attribute can be toggled at runtime to show or hide the button.
 * @property {number} order {@attr order} Z-order of the modal, this property
 * is set automatically.
 * @property {boolean} opened {@attr opened} Sets this attribute to open or close the modal.
 * @property {HTMLDivElement} backdrop
 * @property {HTMLButtonElement} closeAction
 * @property {HTMLButtonElement} backActionButton
 * @property {UIButtonbar} buttonBar
 * @property {HTMLDivElement} modalWindow
 * @property {HTMLElement} modalHeader
 * @property {HTMLDivElement} modalBody
 * @property {HTMLElement} modalFooter
 * @property {HTMLElement} modalTitles
 * @property {HTMLElement} modalTitleElement
 * @property {HTMLElement} modalSubtitleElement
 * @property {HTMLHeadingElement} heading
 * @slot
 * @example
 * <div>
 *   <ui-modal id="super-modal">
 *     <h2>I am modal title</h2>
 *     <p>How are you?</p>
 *   </ui-modal>
 *   <button class="button" onclick="SharedComponents.eventBus.dispatchCustomEvent(
 *     'modal-open', { selector: '#super-modal'});">Toggle</button>
 * </div>
 */
@Component({
    tag: 'ui-modal',
    styles,
})
export class UIModal extends UIElement implements Activatable {
    @Attribute(String) type?: UIModalType;
    @Attribute(String) align?: UIModalAlignment;
    @Attribute(Boolean) detached?: boolean;
    @Attribute(Number) order: number = 1;
    @Attribute(Boolean) compact?: boolean;
    @Attribute(Boolean) closable: boolean = true;
    @Attribute(Boolean) noclosable?: boolean;
    @Attribute(String) layout?: UIModalLayout;
    @Attribute(String) labelAccept?: string;
    @Attribute(String) labelDecline?: string;
    @Attribute(String) labelDismiss?: string;
    @Attribute(Boolean) animation?: boolean;
    @Attribute(String) modalTitle?: string;
    @Attribute(String) modalSubtitle?: string;
    @Attribute(Boolean) bottomSheet?: boolean;
    @Attribute(Boolean) noBottomSheet?: boolean;
    @Attribute(Boolean) backAction?: boolean;
    @Attribute(Boolean) opened?: boolean;

    @Child('.ui-modal__backdrop') backdrop!: HTMLDivElement;
    @Child('.ui-modal__close-action') closeAction!: HTMLButtonElement;
    @Child('.ui-modal__back-action') backActionButton!: HTMLButtonElement;
    @Child('.ui-modal__buttons') buttonBar!: UIButtonbar;
    @Child('.ui-modal__window') modalWindow!: HTMLDivElement;
    @Child('.ui-modal__header') modalHeader!: HTMLElement;
    @Child('.ui-modal__body') modalBody!: HTMLDivElement;
    @Child('.ui-modal__footer') modalFooter!: HTMLElement;
    @Child('.ui-modal__titles') modalTitles!: HTMLDivElement;
    @Child('.ui-modal__title') modalTitleElement!: HTMLSpanElement;
    @Child('.ui-modal__subtitle') modalSubtitleElement!: HTMLSpanElement;
    @Child('.ui-modal__body h2:first-of-type') heading!: HTMLHeadingElement;

    public lockedBy?: NullOr<number>;

    private _headingID?: string;
    private _clientY?: number;
    private _clientX?: number;
    private closeActionHandler!: (
        event?: Event | CustomEvent<unknown>
    ) => void | EventHandler;
    private acceptActionHandler!: (
        event?: Event | CustomEvent<unknown>
    ) => void | EventHandler;
    private declineActionHandler!: (
        event?: Event | CustomEvent<unknown>
    ) => void | EventHandler;
    private backActionHandler!: (
        event?: Event | CustomEvent<unknown>
    ) => void | EventHandler;
    private scrollShadowHander!: EventHandler;
    private handleBackdropInteraction!: EventHandler;
    private static readonly DefaultZIndex = 909;

    private static readonly ContentShadow: UIContentShadow = {
        CSS: {
            Up: '-ui-modal-inner-shadow-up',
            Down: '-ui-modal-inner-shadow-down',
        },
        Offset: 15,
        ThrottleTime: 100,
    };

    static readonly Type: UIModalTypes = {
        Confirmation: 'confirmation',
        Alert: 'alert',
        Popover: 'popover',
        Bubble: 'bubble',
        Menu: 'menu',
    };

    static readonly Layouts: UIModalLayouts = {
        Default: 'default',
        Fluid: 'fluid',
        Logout: 'logout',
    };

    static readonly Alignments: UIModalAlignments = {
        Fixed: 'fixed',
        Top: 'top',
        Middle: 'middle',
        Bottom: 'bottom',
        Left: 'left',
        Right: 'right',
        None: 'none',
    };

    static readonly ActionType: UIModalActionTypes = {
        Accept: 'accept',
        Decline: 'decline',
    };

    static readonly WindowWidth: Record<UIModalWidth, number> = {
        small: 240,
        medium: 320,
        large: 480,
        xlarge: 800,
    };

    static readonly ScrollIncrementSettings: UIModalScrollIncrementSettings = {
        StandardIncrement: 40,
        getPageIncrement: function (scrollableArea: HTMLElement): number {
            return scrollableArea.clientHeight - this.StandardIncrement;
        },
    };

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-modal', {
            accept: 'Yes',
            decline: 'No',
            dismiss: 'OK',
            close: 'Close',
            back: 'Back',
            opened: 'Pop-up window is opened',
            exitLabel: 'Press ESC to close',
        });
    }

    static get observedAttributes(): Array<string> {
        return ['order', 'opened', 'back-action'];
    }

    /**
     * @type {boolean}
     */
    get shifted(): boolean {
        return this.classList.contains('-shifted');
    }

    set shifted(value: boolean) {
        this.updateClassList({ '-shifted': value });
    }

    /**
     * @type {boolean}
     */
    get locked(): boolean {
        return this.classList.contains('-locked');
    }

    set locked(value: boolean) {
        this.updateClassList({ '-locked': value });
    }

    /**
     * @type {string}
     */
    get headingID(): string {
        if (this.heading?.id) {
            return this.heading.id;
        }
        this._headingID ??= `ui-modal-heading-${Digest.uuidv4()}`;
        return this._headingID;
    }

    /**
     * Check if the current modal is active.
     * @returns {boolean} true if modal is active, false otherwise.
     */
    isActive(): boolean {
        return this.classList.contains('-active');
    }

    /**
     * Check if current modal is animated. All modals windows are animatable
     * expect those who has type 'popover' (tooltips, dropdown, etc.).
     * @returns {boolean} true if modal is animated, false otherwise.
     */
    isAnimated(): boolean {
        return !browser.prefersReducedMotion();
    }

    /**
     * Checks if the modal has header (title, subtitle or cross button).
     * @returns {boolean}
     */
    hasHeader(): boolean {
        return !this.compact || !!this.modalTitle || !!this.modalSubtitle;
    }

    /**
     * Checks if the modal has footer (footer slot or buttons for confirmation and alert modals).
     * @returns {boolean}
     */
    hasFooter(): boolean {
        return !!this.querySelector(':scope > footer');
    }

    /**
     * Opens modal window.
     * Use this function for opening modals with the order that equals
     * to the modals order location inside DOM or just for the only one modal on the page.
     * Use 'ui-modal-controller' with 'modal-open' event to follow the right order for several modals.
     * Try to avoid mixing 'modal-open' functionality of 'ui-modal-controller' with this.
     * @fires event:modal-open-enter Fires then modal is just starting to open.
     * @returns {UIModal}
     */
    open(): UIModal {
        if (!this.isConnected && this.hasAttribute('dynamic')) {
            document.body.append(this);
        }

        // With async rendering (config.renderAsync(), used by Angular)
        // render() is deferred to a later frame so a modal created and opened in the
        // same task (e.g. ui-tooltip/ui-annotation popovers) would be exposed and
        // focused before render() moved its content into ui-modal__body.
        // This made screen readers announce the content twice.
        // Render now so the content is settled before opening,
        // matching synchronous rendering.
        if (!this.rendered) {
            this.prerenderElementsTree();
        }

        this.classList.add('-active');
        // reflow trick, do not remove for smoother animation
        this.offsetWidth.toString();
        // Fired after -active so that position handlers (e.g. makePopoverHandler)
        // can read correct offsetHeight from the now-visible modal content.
        dispatchCustomEvent(this, 'modal-open-enter', {});
        this.classList.add('-presented');
        if (this.isAnimated()) {
            waitForAnimations(this.modalWindow)
                .catch(this.onOpen.bind(this))
                .finally(this.onOpen.bind(this));
        } else {
            this.onOpen();
        }
        return this;
    }

    /**
     * Closes modal window.
     * @fires event:modal-close-enter
     * @returns {UIModal}
     */
    close(): UIModal {
        dispatchCustomEvent(this, 'modal-close-enter', {});
        this.classList.remove('-presented');
        // reflow trick, do mot remove for smoother animation
        this.offsetWidth.toString();
        if (this.isAnimated()) {
            waitForAnimations(this.modalWindow)
                .catch(this.onClose.bind(this))
                .finally(this.onClose.bind(this));
        } else {
            this.onClose();
        }
        return this;
    }

    /**
     * Close action callback.
     * @private
     */
    private handleCloseAction(): void {
        if (!this.closable || this.noclosable) {
            return;
        }
        dispatchCustomEvent(this, 'modal-close', { dismiss: true });
    }

    /**
     * Accept action callback.
     * @private
     */
    private handleAcceptAction(): void {
        dispatchCustomEvent(this, 'modal-action', {
            type: UIModal.ActionType.Accept,
        });
    }

    /**
     * Decline action callback.
     * @private
     */
    private handleDeclineAction(): void {
        dispatchCustomEvent(this, 'modal-action', {
            type: UIModal.ActionType.Decline,
        });
    }

    /**
     * Back action callback.
     * @private
     * @fires event:modal-back
     */
    private handleBackAction(): void {
        dispatchCustomEvent(this, 'modal-back', {});
    }

    /**
     * Decline action callback.
     * @private
     */
    private onOpen(): void {
        this.opened = true;
        this.modalFooter?.classList.toggle(
            UIModal.ContentShadow.CSS.Up,
            this.modalBody.scrollHeight > this.modalBody.offsetHeight
        );
        this.setInitialFocus();
        dispatchCustomEvent(this, 'modal-updatelist', {
            action: UIModalController.UpdateActions.Add,
        });
    }

    /**
     * Decline action callback.
     * @private
     */
    private onClose(): void {
        this.opened = false;
        this.classList.remove('-active');
        dispatchCustomEvent(this, 'modal-updatelist', {
            action: UIModalController.UpdateActions.Remove,
        });
        if (this.hasAttribute('dynamic')) {
            this.parentElement?.removeChild(this);
        } else {
            this.setAttributes({ style: null, order: '1' });
        }
    }

    /**
     * @returns {Array<HTMLElement>}
     */
    getInteractiveElements(): Array<HTMLElement> {
        const targets = [
            ...PrimaryInteractiveElements,
            ...SecondaryInteractiveElements,
        ].join(',');
        return Array.from(this.querySelectorAll<HTMLElement>(targets)).filter(
            isVisible
        );
    }

    /**
     * Checks if there is at least one interactive element.
     * @private
     * @returns {boolean} true if there is at least one interactive element, false otherwise.
     */
    private hasInteractiveElements(): boolean {
        return this.getInteractiveElements().length > 0;
    }

    /**
     * Prevents the body scrolling for iOS devices.
     */
    disableBodyScroll(): void {
        const isElementHorizontallyScrolled = (
            element: HTMLElement,
            shiftX: number
        ): boolean => {
            if (
                element.clientWidth >= element.scrollWidth ||
                (element.scrollLeft === 0 && -Math.abs(shiftX) > 0)
            ) {
                return false;
            }
            const scrolledX =
                element.scrollWidth - element.scrollLeft <= element.clientWidth;
            return !(scrolledX && shiftX < 0);
        };

        const isElementVerticallyScrolled = (
            element: HTMLElement,
            shiftY: number
        ): boolean => {
            if (
                element.clientHeight >= element.scrollHeight ||
                (element.scrollTop === 0 && -Math.abs(shiftY) > 0)
            ) {
                return false;
            }
            const scrolledY =
                element.scrollHeight - element.scrollTop <=
                element.clientHeight;
            return !(scrolledY && shiftY < 0);
        };

        const cancelEvent = (event: TouchEvent): void => {
            if (!event.cancelable) {
                return;
            }
            try {
                event.preventDefault();
            } catch (e) {
                // no luck, scroll in progress etc...
            }
        };

        this.addEventListener(
            'touchstart',
            (event: TouchEvent) => {
                if (event.targetTouches.length !== 1) {
                    return;
                }
                this._clientY = event.targetTouches[0].clientY;
                this._clientX = event.targetTouches[0].clientX;
            },
            {
                passive: true,
                capture: false,
            }
        );

        this.addEventListener(
            'touchmove',
            (event: TouchEvent) => {
                if (event.targetTouches.length !== 1) {
                    return;
                }
                if (!isParentOf(this.modalBody, event.target as HTMLElement)) {
                    cancelEvent(event);
                    return;
                }
                const shiftY =
                    event.targetTouches[0].clientY - (this._clientY ?? 0);
                const shiftX =
                    event.targetTouches[0].clientX - (this._clientX ?? 0);
                if (Math.abs(shiftY) >= Math.abs(shiftX)) {
                    const yScrollableTarget = closestByCond(
                        event.target as HTMLElement,
                        (node: HTMLElement | ParentNode) => {
                            return (
                                node === this.modalBody ||
                                (node instanceof HTMLElement &&
                                    node.clientHeight < node.scrollHeight)
                            );
                        }
                    );
                    if (
                        !isElementVerticallyScrolled(
                            yScrollableTarget as HTMLElement,
                            shiftY
                        )
                    ) {
                        cancelEvent(event);
                    }
                } else {
                    const xScrollableTarget = closestByCond(
                        event.target as HTMLElement,
                        (node: HTMLElement | ParentNode) => {
                            return (
                                node === this.modalBody ||
                                (node instanceof HTMLElement &&
                                    node.clientWidth < node.scrollWidth)
                            );
                        }
                    );
                    if (
                        !isElementHorizontallyScrolled(
                            xScrollableTarget as HTMLElement,
                            shiftX
                        )
                    ) {
                        cancelEvent(event);
                    }
                }
            },
            {
                passive: false,
                capture: false,
            }
        );
    }

    /**
     * Changes current view to active (open) state.
     * @returns {UIModal}
     */
    activate(): UIModal {
        return this.open();
    }

    observeAttributes(
        name: string,
        oldValue: string | null,
        newValue: string | null
    ): void {
        if (newValue === oldValue) {
            return;
        }
        switch (name) {
            case 'order':
                if (this.order > 1) {
                    this.style.zIndex = String(
                        UIModal.DefaultZIndex + this.order
                    );
                }
                break;
            case 'opened':
                if (newValue !== null) {
                    // if component is not attached to DOM yet, open it
                    // asynchronously on next frame. Otherwise, just open it
                    // as normal in synchronous way.
                    const isAttachedToDOM =
                        this.state !== null ||
                        this.state === ComponentState.Hydrated;
                    if (!isAttachedToDOM) {
                        runInNextFrame(this.openModalByEvent.bind(this));
                    } else {
                        this.openModalByEvent();
                    }
                } else if (this.isActive()) {
                    this.closeModalByEvent();
                }
                break;
            case 'back-action':
                // Only react once the header exists; the initial value is handled
                // by render() when it builds the header.
                if (this.rendered) {
                    this.toggleBackActionButton(newValue !== null);
                }
                break;
            default:
                break;
        }
    }

    /**
     * Opens modal by dispatching 'modal-open' event.
     * @private
     */
    private openModalByEvent(): void {
        dispatchCustomEvent(this, 'modal-open', {
            selector: `#${this.id}`,
        });
        this.updateModalBodyTabIndex();
    }

    /**
     * Updates modal body tabindex based on scrollability.
     * @private
     */
    private updateModalBodyTabIndex(): void {
        if (!this.modalBody) {
            return;
        }
        // For "popover" modals (ui-tooltip, ui-annotation): keep their body out
        // of the tab order so there isn't an extra stop before the
        // close button that re-announces the content. Scoped to Popover so
        // Bubble/Menu (e.g. dropdowns) are unaffected.
        if (this.type === UIModal.Type.Popover) {
            this.modalBody.removeAttribute('tabindex');
            return;
        }
        const isScrollable =
            this.modalBody.scrollHeight > this.modalBody.clientHeight;
        if (isScrollable) {
            this.modalBody.setAttribute('tabindex', '0');
        } else {
            this.modalBody.removeAttribute('tabindex');
        }
    }

    /**
     * Closes modal by dispatching 'modal-close' event.
     * @private
     */
    private closeModalByEvent(): void {
        dispatchCustomEvent(this, 'modal-close', {});
    }

    /**
     * Sets the ID of the element if not set.
     * @private
     */
    private setUniqueId(): void {
        this.id ||= `ui-modal-${Digest.randomId()}`;
    }

    /**
     * Checks that modals is type of "popover" or "bubble", in other words
     * it is a small dialog.
     */
    isPopover(): boolean {
        if (!this.type) {
            return false;
        }
        return (
            [
                UIModal.Type.Popover,
                UIModal.Type.Bubble,
                UIModal.Type.Menu,
            ] as Array<UIModalType>
        ).includes(this.type);
    }

    /**
     * Builds modal header.
     * @private
     * @returns {ElementConfig}
     */
    private buildModalHeader(): ElementConfig {
        const closeButton: ElementConfig = {
            tagName: 'div',
            classList: {
                'ui-modal__close-action-container': true,
            },
            children: !this.compact
                ? ([
                      {
                          tagName: 'ui-button',
                          classList: {
                              'ui-modal__close-action': true,
                          },
                          attributes: {
                              'type': 'button',
                              'aria-label': UIModal.labels.close,
                              'title': UIModal.labels.close,
                              'layout': 'tertiary',
                              'glyph': 'redesign-x-mark',
                          },
                      },
                  ] as Array<ElementChild>)
                : [],
        };

        const titlesChildren: Array<ElementChild> = [];
        if (this.modalTitle) {
            titlesChildren.push({
                tagName: 'span',
                classList: {
                    'ui-modal__title': true,
                },
                children: [this.modalTitle],
            });
        }
        if (this.modalSubtitle) {
            titlesChildren.push({
                tagName: 'span',
                classList: {
                    'ui-modal__subtitle': true,
                },
                children: [this.modalSubtitle],
            });
        }

        const titles: ElementConfig = {
            tagName: 'div',
            classList: {
                'ui-modal__titles': true,
            },
            children: titlesChildren,
        };

        // The container is always rendered so it keeps balancing the close button
        // and keeps the titles centered. The button itself is opt-in via the
        // 'back-action' attribute and can be toggled at runtime (see
        // toggleBackActionButton), so it does not appear on modals that don't ask for it.
        const backButton: ElementConfig = {
            tagName: 'div',
            classList: {
                'ui-modal__back-action-container': true,
            },
            children: this.backAction ? [this.buildBackActionButton()] : [],
        };

        return {
            tagName: 'header',
            classList: {
                'ui-modal__header': true,
            },
            children: [backButton, titles, closeButton],
        };
    }

    /**
     * Builds the back button placed inside the header back-action container.
     * @private
     * @returns {ElementConfig}
     */
    private buildBackActionButton(): ElementConfig {
        return {
            tagName: 'ui-button',
            classList: {
                'ui-modal__back-action': true,
            },
            attributes: {
                'type': 'button',
                'aria-label': UIModal.labels.back,
                'title': UIModal.labels.back,
                'layout': 'tertiary',
                'glyph': 'redesign-navigate-back',
            },
        };
    }

    /**
     * Adds or removes the header back button at runtime, mirroring the
     * 'back-action' attribute. Called from observeAttributes so consumers can
     * toggle the button between modal views without re-rendering the modal.
     * @private
     * @param {boolean} show
     */
    private toggleBackActionButton(show: boolean): void {
        const container = this.modalWindow?.querySelector(
            '.ui-modal__back-action-container'
        );
        if (!container) {
            return;
        }
        if (show && !this.backActionButton) {
            const button = this.createElement(this.buildBackActionButton());
            updateElement(button, {
                events: {
                    click: this.backActionHandler,
                },
            });
            container.appendChild(button);
        } else if (!show && this.backActionButton) {
            this.backActionButton.remove();
        }
    }

    /**
     * Builds modal body.
     * @private
     * @returns {ElementConfig}
     */
    private buildModalBody(): ElementConfig {
        return {
            tagName: 'div',
            classList: {
                'ui-modal__body': true,
            },
            children: this.childNodes,
        };
    }

    /**
     * Builds modal footer.
     * @private
     * @returns {ElementConfig}
     */
    private buildModalFooter(): ElementConfig {
        const footer = this.querySelector(':scope > footer');
        const elementConfig: ElementConfig = {
            tagName: 'footer',
            classList: {
                'ui-modal__footer': true,
            },
        };
        if (footer) {
            elementConfig.element = footer as HTMLElement;
        }
        return elementConfig;
    }

    /**
     * Builds modal backdrop with proper accessibility attributes.
     * @private
     * @returns {ElementConfig}
     */
    private buildModalBackdrop(): ElementConfig {
        const isClosableBackdrop =
            this.isClosable() &&
            this.type !== UIModal.Type.Popover &&
            !(this.type === UIModal.Type.Menu && this.noBottomSheet);

        return {
            tagName: 'div',
            classList: {
                'ui-modal__backdrop': true,
            },
            attributes: isClosableBackdrop
                ? {
                      'tabindex': '0',
                      'aria-label': `${UIModal.labels.opened}, ${UIModal.labels.exitLabel}`,
                  }
                : {
                      'aria-hidden': 'true',
                  },
        };
    }

    /**
     * Sets initial focus when modal opens
     * @private
     */
    private setInitialFocus(): void {
        const interactiveElements = this.getInteractiveElements();
        if (this.hasInteractiveElements()) {
            interactiveElements[0].focus();
        } else {
            this.focus();
        }
    }

    /**
     * Draws shadows for the header and footer.
     * @private
     * @param {Event} event
     */
    private handleScrolledShadow(event: Event): void {
        const elem = event.target as HTMLElement;
        const isAtStart = elem.scrollTop > UIModal.ContentShadow.Offset;
        const isAtEnd =
            elem.offsetHeight + elem.scrollTop >=
            elem.scrollHeight - UIModal.ContentShadow.Offset;
        this.modalHeader?.classList.toggle(
            UIModal.ContentShadow.CSS.Down,
            isAtStart
        );
        this.modalFooter?.classList.toggle(
            UIModal.ContentShadow.CSS.Up,
            !isAtEnd
        );
    }

    /**
     * @private
     */
    private setupA11y(): void {
        let hasTitle = true;
        if (this.heading) {
            this.heading.id = this.headingID;
        } else if (this.modalTitle) {
            this.modalTitleElement.id = this.headingID;
        } else if (this.modalSubtitle) {
            this.modalSubtitleElement.id = this.headingID;
        } else {
            hasTitle = false;
        }
        if (hasTitle) {
            this.setAttribute('aria-labelledby', this.headingID);
        }
    }

    /**
     * Is modal can be closed. This method is used for legacy compatibility.
     * @returns {boolean}
     */
    isClosable(): boolean {
        return (this.closable ?? true) && !this.noclosable;
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.setUniqueId();
        const hasBottomSheet =
            (this.type === UIModal.Type.Menu && !this.noBottomSheet) ||
            this.layout === UIModal.Layouts.Logout ||
            this.bottomSheet ||
            false;

        // Only set role and aria-modal if not already specified
        const attributes: Attributes = {
            'tabindex': TabIndex.Inactive,
            'bottom-sheet': hasBottomSheet,
        };

        if (!this.hasAttribute('role')) {
            attributes.role = 'dialog';
        }

        // Don't add aria-modal if role is 'presentation' (presentational elements don't need aria-modal)
        if (
            !this.hasAttribute('aria-modal') &&
            this.getAttribute('role') !== 'presentation'
        ) {
            attributes['aria-modal'] = 'true';
        }

        this.setAttributes(attributes);
        this.updateClassList({
            '-shifted': this.shifted,
        });

        this.insertElements([
            this.buildModalBackdrop(),
            {
                tagName: 'div',
                classList: {
                    'ui-modal__window': true,
                    '-alert': this.type === UIModal.Type.Alert,
                    '-confirmation': this.type === UIModal.Type.Confirmation,
                },
                children: [
                    ...(this.type !== UIModal.Type.Popover &&
                    this.type !== UIModal.Type.Bubble &&
                    this.hasHeader()
                        ? [this.buildModalHeader()]
                        : []),
                    this.buildModalBody(),
                    ...(this.type !== UIModal.Type.Popover &&
                    this.type !== UIModal.Type.Bubble &&
                    this.hasFooter()
                        ? [this.buildModalFooter()]
                        : []),
                ],
            },
        ]);

        if (this.type === UIModal.Type.Alert) {
            this.setAttributes({
                role: 'alertdialog',
                layout: UIModal.Layouts.Default,
            });
            if (!this.modalFooter) {
                this.modalWindow?.appendChild(
                    this.createElement(this.buildModalFooter())
                );
            }
            this.modalFooter.appendChild(
                this.createElement({
                    tagName: 'ui-buttonbar',
                    classList: {
                        'ui-modal__buttons': true,
                    },
                    children: [
                        {
                            tagName: 'button',
                            classList: { 'button': true, '-dismiss': true },
                            attributes: {
                                type: 'button',
                            },
                            children: [
                                this.labelDismiss || UIModal.labels.dismiss,
                            ],
                        },
                    ],
                })
            );
        }

        if (this.type === UIModal.Type.Confirmation) {
            this.setAttributes({
                layout: UIModal.Layouts.Default,
            });
            if (!this.modalFooter) {
                this.modalWindow?.appendChild(
                    this.createElement(this.buildModalFooter())
                );
            }
            this.modalFooter.appendChild(
                this.createElement({
                    tagName: 'ui-buttonbar',
                    classList: {
                        'ui-modal__buttons': true,
                    },
                    children: [
                        {
                            tagName: 'button',
                            classList: {
                                'button': true,
                                '-destructive': true,
                                '-decline': true,
                            },
                            attributes: {
                                type: 'button',
                            },
                            children: [
                                this.labelDecline || UIModal.labels.decline,
                            ],
                        },
                        {
                            tagName: 'button',
                            classList: {
                                'button': true,
                                '-accept': true,
                            },
                            attributes: {
                                type: 'button',
                            },
                            children: [
                                this.labelAccept || UIModal.labels.accept,
                            ],
                        },
                    ],
                })
            );
        }
        this.setupA11y();
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.closeActionHandler = this.handleCloseAction.bind(this);
        this.acceptActionHandler = this.handleAcceptAction.bind(this);
        this.declineActionHandler = this.handleDeclineAction.bind(this);
        this.backActionHandler = this.handleBackAction.bind(this);
        this.scrollShadowHander = this.isAnimated()
            ? throttle(
                  this.handleScrolledShadow.bind(this) as () => void,
                  UIModal.ContentShadow.ThrottleTime
              )
            : this.handleScrolledShadow.bind(this);

        if (this.type !== UIModal.Type.Popover) {
            this.handleBackdropInteraction = (event: Event) => {
                if (
                    event.type === 'click' ||
                    (event.type === 'keydown' &&
                        (event as KeyboardEvent).key === 'Enter')
                ) {
                    if (event.type === 'keydown') {
                        event.preventDefault();
                    }
                    this.handleCloseAction();
                }
            };
            this.backdrop.addEventListener(
                'click',
                this.handleBackdropInteraction
            );
            this.backdrop.addEventListener(
                'keydown',
                this.handleBackdropInteraction
            );
        }

        if (this.closeAction) {
            updateElement(this.closeAction, {
                events: {
                    click: this.closeActionHandler,
                },
            });
        }

        if (this.backActionButton) {
            updateElement(this.backActionButton, {
                events: {
                    click: this.backActionHandler,
                },
            });
        }

        if (this.type === UIModal.Type.Alert) {
            const dismissButton =
                this.buttonBar.querySelector('button.-dismiss');
            if (dismissButton) {
                updateElement(dismissButton, {
                    events: {
                        click: this.closeActionHandler,
                    },
                });
            }
        }

        if (this.type === UIModal.Type.Confirmation) {
            const acceptButton = this.buttonBar.querySelector('button.-accept');
            const declineButton =
                this.buttonBar.querySelector('button.-decline');
            if (acceptButton) {
                updateElement(acceptButton, {
                    events: {
                        click: this.acceptActionHandler,
                    },
                });
            }
            if (declineButton) {
                updateElement(declineButton, {
                    events: {
                        click: this.declineActionHandler,
                    },
                });
            }
        }

        this.disableBodyScroll();
        this.modalBody.addEventListener('scroll', this.scrollShadowHander);
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        if (this.isActive()) {
            dispatchCustomEvent(document.body, 'modal-updatelist', {
                action: UIModalController.UpdateActions.Remove,
            });
        }
        if (this.scrollShadowHander) {
            this.modalBody?.removeEventListener(
                'scroll',
                this.scrollShadowHander
            );
        }
        if (
            this.type !== UIModal.Type.Popover &&
            this.backdrop &&
            this.handleBackdropInteraction
        ) {
            this.backdrop.removeEventListener(
                'click',
                this.handleBackdropInteraction
            );
            this.backdrop.removeEventListener(
                'keydown',
                this.handleBackdropInteraction
            );
        }
    }
}
